/**
 * Marketplace logos and the country detection that picks them. The data lives
 * in public/marketplaces/ because the free photo resizer tool (public/tools/)
 * reads the same file.
 */
export interface Marketplace {
  name: string;
  icon: string;
  ratio: string;
  /** ISO country codes; empty means shown everywhere. */
  countries: string[];
}

export interface MarketplaceData {
  marketplaces: Marketplace[];
  timezones: Record<string, string>;
  languages: Record<string, string>;
}

export async function fetchMarketplaceData(): Promise<MarketplaceData> {
  const response = await fetch("/marketplaces/marketplaces.json");
  if (!response.ok) {
    throw new Error(`Marketplaces request failed: ${response.status}`);
  }
  return (await response.json()) as MarketplaceData;
}

// Timezone first: browser languages are often en-US regardless of country, and
// unlike geo-IP it survives VPNs. Then the language picked in the app (an
// explicit choice), then the browser's.
export function detectCountry(
  data: MarketplaceData,
  appLanguage: string,
): string | null {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (data.timezones[timeZone]) return data.timezones[timeZone];
  if (data.languages[appLanguage]) return data.languages[appLanguage];
  const known = new Set(data.marketplaces.flatMap((m) => m.countries));
  for (const locale of navigator.languages) {
    const [language, region] = locale.split("-");
    if (region && known.has(region.toUpperCase())) return region.toUpperCase();
    if (data.languages[language]) return data.languages[language];
  }
  return null;
}
